import { MapContainer, CircleMarker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { useState, useEffect, useCallback, useRef } from 'react';
import MapInteraction from './MapInteraction';
import LayersControlPanel from './LayersControlPanel';
import FilterMenu from '../Filter/FilterMenu';
import filterIcon from '../logos/filter.png';
import calendarIcon from '../logos/calendar.png';
import contactIcon from '../logos/contact.png';
import Calendar from '../Calendar/Calendar';
import ContactForm from '../ContactForm/ContactForm';
import MapButton from './MapButton';
import TourControl from '../Tour/TourControl';
import TourDisplay from '../Tour/TourDisplay';
import EventModal from '../Calendar/EventModal';
import Overlay from '../common/Overlay';
import Attribution from './Attribution';
import MapMarkers from './MapMarkers';
import flyToLocation from '../utils/flyToLocation';
import buildLocationContent from '../utils/buildLocationContent';
import { useUserLocation } from './useUserLocation';
import './Map.css';

function MapView({ setIsMarkerSelected, content, setContent, isContentCollapsed }) {
  
  const mapRef = useRef();
  const initialMobilePos = [48.998, 12.095];
  const initialDesktopPos = [48.998, 12.115];

  // State for active component
  const [activeComponent, setActiveComponent] = useState(null);
  
  const [showCategoryMarker, setShowCategoryMarker] = useState({});
  const [locations, setLocations] = useState([]);
  const [events, setEvents] = useState([]);
  const [tours, setTours] = useState([]);
  const [mapButtonsVisibility, setMapButtonsVisibility] = useState([]);
  const [activeTour, setActiveTour] = useState(null);
  const [activeTourMarkers, setActiveTourMarkers] = useState(new Set());
  const [isTourModalOpen, setIsTourModalOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [filterOverlayPos, setFilterOverlayPos] = useState({ top: 70, left: 10 });
  const [categoryLocations, setCategoryLocations] = useState([]);
  const [subcategoryLocations, setSubcategoryLocations] = useState([]);

  // Computed properties based on activeComponent
  const showFilter = activeComponent === 'filter';
  const showCalendar = activeComponent === 'calendar';
  const showTourList = activeComponent === 'tours';
  const showContactForm = activeComponent === 'contact';
  const { userPosition } = useUserLocation();

  const updateFilterOverlayPos = useCallback(() => {
    const btn = document.querySelector('.map-button-filter .map-button');
    if (!btn) return;

    const rect = btn.getBoundingClientRect();
    const top = Math.round(rect.top);
    const left = Math.round(rect.left);
    setFilterOverlayPos({ top, left });
  }, []);

  // Retrieve the current tour loaction
  const currentTourLocation = activeTour?.locations?.[activeIndex] ?? null;

  
    // Fly to location when selecting from the FilterMenu
  useEffect(() => {
    if (currentTourLocation) flyToLocation(mapRef.current, currentTourLocation.latitude, currentTourLocation.longitude, 17);
  }, [currentTourLocation])
  
  // Fly to active tour location during tours
  useEffect(() => {
    if (content) flyToLocation(mapRef.current, content.latitude, content.longitude, 17);
  }, [content]);


  // Fetch information about which MapButtons should be visible
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_BASE_URL}/api/map-button-control`)
      .then((response) => {
        if (!response.ok) throw new Error('Fehler beim Abrufen der Daten');
        return response.json();
      })
      .then((res) => {
        setMapButtonsVisibility(res.data);
      })
      .catch((error) => console.error('Fehler:', error));
  }, []);

  // Fetch Locations and populate images, categories, subcategories, and locationInfo
  useEffect(() => {
    fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/locations?populate[images][populate]=image&populate[category]=true&populate[subcategory][populate]=category&populate[location_infos][populate]=info_type`
    )
      .then((response) => {
        if (!response.ok) throw new Error('Fehler beim Abrufen der Daten');
        return response.json();
      })
      .then((res) => {

        // Maps to store unique categories and subcategories
        const categoryMap = new Map();
        const subcategoryMap = new Map();

        // Objects to store locations grouped by category/subcategory
        const catLocations = {};
        const subcatLocations = {};

        res.data.forEach((item) => {
          const category = item.category;
          const subcategory = item.subcategory;

          if (category) {
            if (!categoryMap.has(category.id)) {
              categoryMap.set(category.id, category);
            }

            // If location doesn't have a subcategory, assign it directly to the category
            if (!subcategory) {
              if (!catLocations[category.id]) catLocations[category.id] = [];
              catLocations[category.id].push(item);
            }
          }

          if (subcategory) {
            if (!subcategoryMap.has(subcategory.id)) {
              subcategoryMap.set(subcategory.id, subcategory);
            }

            if (!subcatLocations[subcategory.id]) subcatLocations[subcategory.id] = [];
            subcatLocations[subcategory.id].push(item);
          }
        });

        setCategories(Array.from(categoryMap.values()));
        setSubcategories(Array.from(subcategoryMap.values()));
        setCategoryLocations(catLocations);
        setSubcategoryLocations(subcatLocations);

        // Initialize visibility state for all categories and subcategories
        const initialVisibility = {};
        categoryMap.forEach((cat) => {
          initialVisibility[cat.id] = cat.initiallyVisible ?? true;
        });
        subcategoryMap.forEach((sub) => {
          initialVisibility[sub.id] = sub.initiallyVisible ?? true;
        });
        setShowCategoryMarker(initialVisibility);

        setLocations(res.data);
        setActiveTourMarkers(new Set(res.data.map((l) => l.id)));
      })
      .catch((error) => console.error('Fehler:', error));
  }, []);

  // Fetch Events
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_BASE_URL}/api/events?populate[dayTimeOverride][populate]=*`)
      .then((response) => {
        if (!response.ok) throw new Error('Fehler beim Abrufen der Daten');
        return response.json();
      })
      .then((res) => setEvents(res.data))
      .catch((error) => console.error('Fehler:', error));
  }, []);

  // Fetch Tours
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tours?populate[images][populate]=image&populate[locations][populate][0]=category&populate[locations][populate][1]=images.image&populate[locations][populate][2]=subcategory.category&populate[locations][populate][3]=location_infos.info_type&populate[location_infos][populate]=info_type`)
      .then((response) => {
        if (!response.ok) throw new Error('Fehler beim Abrufen der Daten');
        return response.json();
      })
      .then((res) => setTours(res.data))
      .catch((error) => console.error('Fehler:', error));
  }, []);


  useEffect(() => {
    // Only run if the filter overlay is currently visible
    if (!showFilter) return;

    updateFilterOverlayPos();

    // Recalculate overlay position whenever the window is resized/scrolled
    window.addEventListener('resize', updateFilterOverlayPos);
    window.addEventListener('scroll', updateFilterOverlayPos, true);

    // Cleanup: remove listeners
    return () => {
      window.removeEventListener('resize', updateFilterOverlayPos);
      window.removeEventListener('scroll', updateFilterOverlayPos, true);
    };
  }, [showFilter, updateFilterOverlayPos]);

  // Set the ContentView to the current tour location when it updates
  useEffect(() => {
    if (currentTourLocation && window.innerWidth >= 769) handleMarkerSelection(currentTourLocation);
  }, [currentTourLocation]);

  // Handlers for button clicks with toggle functionality
  const handleFilterClick = () => {
    setActiveComponent(prev => prev === 'filter' ? null : 'filter');
  };

  const handleCalendarClick = () => {
    setActiveComponent(prev => prev === 'calendar' ? null : 'calendar');
  };

  const handleToursToggle = (isOpen) => {
    setActiveComponent(isOpen ? 'tours' : null);
  };

  const handleContactFormClick = (isOpen) => {
    setActiveComponent(isOpen ? 'contact' : null);
  };

  const closeAllOverlays = () => {
    setActiveComponent(null);
  };

  const handleTourSelect = (tour) => {
    setActiveTour(tour);
    setActiveTourMarkers(new Set(tour.locations.map((loc) => loc.id)));
    setActiveIndex(0);
    closeAllOverlays();
  };

  const handleTourEnd = () => {
    setActiveTour(null);
    setActiveTourMarkers(new Set(locations.map((l) => l.id)));
    setActiveIndex(0);
    setIsMarkerSelected(false);
  };

  // Handle the selection of a marker
  const handleMarkerSelection = (loc) => {
    setContent(buildLocationContent(loc));
    setIsMarkerSelected(true);
    closeAllOverlays();
  };

  return (
    <div className="MapView">

      {/* The display for active tours */}
      <TourDisplay
        currentLocation={currentTourLocation}
        activeIndex={activeIndex}
        total={activeTour ? activeTour.locations.length : 0}
        onClose={handleTourEnd}
        onPrev={() => setActiveIndex((i) => i - 1)}
        onNext={() => setActiveIndex((i) => i + 1)}
        isContentCollapsed={isContentCollapsed}
        handleMarkerSelection={handleMarkerSelection}
      />

      <MapContainer
        ref={mapRef}
        className="Map"
        center={window.innerWidth <= 769 ? initialMobilePos : initialDesktopPos}
        zoom={15}
        zoomControl={false}
        attributionControl={false}
      >
        <Attribution />
        <LayersControlPanel />

        {userPosition && (
          <CircleMarker
            center={[userPosition.coords.latitude, userPosition.coords.longitude]}
            radius={8}
            className="UserLocationMarker"
          />
        )}

        {/* Button for the filters */}
        <MapButton
          icon={filterIcon}
          title="Kategorien"
          controlClass="map-button-filter"
          onClick={handleFilterClick}
        />

        {/* Contact form button */}
        {mapButtonsVisibility.showContactForm && (
          <MapButton
            icon={contactIcon}
            title="Kontaktformular"
            onClick={() => handleContactFormClick(true)}
          />
        )}
        
        {/* Calendar button */}
        {mapButtonsVisibility.showCalendar && (
          <MapButton
            icon={calendarIcon}
            title="Kalender"
            onClick={handleCalendarClick}
          />
        )}

        {/* Tour controls */}
        {mapButtonsVisibility.showTours && (
          <TourControl 
            tours={tours} 
            onSelect={handleTourSelect}
            showList={showTourList}
            onToggleList={handleToursToggle}
            setIsMarkerSelected={setIsMarkerSelected}
            setIsTourModalOpen={setIsTourModalOpen}
          />
        )}

        {/* All markers on the map */}
        <MapMarkers
          locations={locations}
          categories={categories}
          showCategoryMarker={showCategoryMarker}
          activeTour={activeTour}
          activeTourMarkers={activeTourMarkers}
          currentTourLocation={currentTourLocation}
          handleMarkerSelection={handleMarkerSelection}
        />

        {/* Handling the FilterMenu */}
        {showFilter && (
          <Overlay
            onClose={closeAllOverlays}
            darkBackground={false}
            style={{
              top: `${filterOverlayPos.top}px`,
              left: `${filterOverlayPos.left}px`,
            }}
          >
            <FilterMenu
              categories={categories}
              subcategories={subcategories}
              showCategory={showCategoryMarker}
              setShowCategory={setShowCategoryMarker}
              categoryLocations={categoryLocations}
              subcategoryLocations={subcategoryLocations}
              onSelectLocation={handleMarkerSelection}
            />
          </Overlay>
        )}

        {/* Handling the calendar */}
        {showCalendar && (
          <Overlay
            onClose={closeAllOverlays}
            darkBackground={true}
          >
            <Calendar
              events={events}
              onEventClick={(event) => {
                setSelectedEvent(event);
                setShowEventModal(true);
                setActiveComponent(null);
              }}
              onClick={closeAllOverlays}
            />
          </Overlay>
        )}

        {/* Handling the contact form */}
        {showContactForm && (
          <Overlay
            onClose={closeAllOverlays}
            darkBackground={true}
            style={{
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)'
            }}
          >
            <ContactForm
              closeAllOverlays={closeAllOverlays}
            />
          </Overlay>
        )}

        {/* Handling events picked from the calendar */}
        {showEventModal && selectedEvent && (
          <Overlay
            onClose={closeAllOverlays}
            darkBackground={true}
          >
            <EventModal
              event={selectedEvent}
              onClose={() => {
                setShowEventModal(false);
                setSelectedEvent(null);
              }}
            />
          </Overlay>
        )}
        <MapInteraction
          disableMapInteractions={
            activeComponent !== null || showEventModal || isTourModalOpen
          }
        />
      </MapContainer>
    </div>
  );
}

export default MapView;